import { Component, inject, ViewChild } from '@angular/core';
import { AiToolHeaderFormArrayComponent } from '../tools/ai-tool-header-form-array/ai-tool-header-form-array.component';
import {
  FunctionApiService,
  FunctionAuthStrategy,
  FunctionInterface,
  ListMCPsRequest,
  ListMCPToolsRequest,
  Namespace,
  UpsertMCPRequest,
} from '@vendasta/ai-assistants';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { httpUrlValidator } from '../ai-function-form/ai-function-form.component';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { ConsentAdminApiService } from '@vendasta/sso';
import { Observable, switchMap } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatOptionModule } from '@angular/material/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { AiToolHeaderFormArray } from '../../core/forms';

class MCPFormControls {
  url: FormControl<string>;
  name: FormControl<string>;
  useVendastaAuth: FormControl<boolean>;
  headers: AiToolHeaderFormArray;
  vendastaAuthScopes: FormControl<string[]>;

  constructor() {
    this.url = new FormControl('', [Validators.required, httpUrlValidator()]);
    this.name = new FormControl('', Validators.required);
    this.useVendastaAuth = new FormControl(false);
    this.headers = new AiToolHeaderFormArray([]);
    this.vendastaAuthScopes = new FormControl<string[]>([]);
  }

  valid(): boolean {
    return this.url.valid && this.name.valid && this.headers.valid && this.vendastaAuthScopes.valid;
  }

  get control(): FormGroup {
    return new FormGroup<any>({
      url: this.url,
      name: this.name,
      useVendastaAuth: this.useVendastaAuth,
      headers: this.headers,
      vendastaAuthScopes: this.vendastaAuthScopes,
    });
  }
}

@Component({
  selector: 'ai-mcp-import-form',
  imports: [
    CommonModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatInputModule,
    TranslateModule,
    MatButtonModule,
    MatMenuModule,
    MatCheckboxModule,
    AiToolHeaderFormArrayComponent,
    GalaxyTooltipModule,
    MatListModule,
    MatOptionModule,
    MatSelectModule,
    MatDialogModule,
    MatStepperModule,
  ],
  templateUrl: './ai-mcp-import-form.component.html',
  styleUrl: './ai-mcp-import-form.component.scss',
  providers: [],
})
export class AiMcpImportFormComponent {
  private fb = inject(FormBuilder);
  private scopes = inject(ConsentAdminApiService);
  private functionService = inject(FunctionApiService);
  protected data: { namespace: Namespace } = inject(MAT_DIALOG_DATA);
  public dialogRef = inject(MatDialogRef<AiMcpImportFormComponent>);
  @ViewChild('stepper') stepper: MatStepper;
  private snackBar = inject(SnackbarService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private translate = inject(TranslateService);

  scopeOptions$: Observable<string[]> = this.scopes.listScopes({ pageSize: 1000 }).pipe(
    take(1),
    map((resp) => resp?.scopes?.map((s) => s.id)),
    shareReplay(),
  );

  functions: FunctionInterface[] = [];
  selectedFunctionIds: Set<string> = new Set();
  mcpExists = false;

  protected form: MCPFormControls = new MCPFormControls();

  vendastaAuthChange(checked: boolean): void {
    if (checked) {
      this.form.vendastaAuthScopes.setValidators([Validators.required]);
    } else {
      this.form.vendastaAuthScopes.clearValidators();
    }
    this.form.vendastaAuthScopes.updateValueAndValidity();
  }

  loadFunctions(): void {
    const name = this.form.name.value;
    const url = this.form.url.value;
    const headers = this.form.headers.toFunctionHeaders();
    const useVendastaAuth = this.form.useVendastaAuth.value;
    const vendastaAuthScopes = this.form.vendastaAuthScopes.value;
    const authStrategy = new FunctionAuthStrategy(
      useVendastaAuth ? { platformManaged: { requiredScopes: vendastaAuthScopes } } : { unspecified: {} },
    );

    this.functionService
      .listMcPs(
        new ListMCPsRequest({
          filters: {
            namespaces: [this.data.namespace],
            mcpIds: [name],
          },
          pagingOptions: {
            pageSize: 1,
          },
        }),
      )
      .pipe(
        switchMap((response) => {
          this.mcpExists = response?.mcps?.length > 0;
          return this.functionService.listMcpTools(
            new ListMCPToolsRequest({
              namespace: this.data.namespace,
              mcpId: name,
              url: url,
              headers: headers,
              authStrategy: authStrategy,
            }),
          );
        }),
      )
      .subscribe({
        next: (result) => {
          this.functions = result?.functions || [];
          this.selectedFunctionIds = new Set(this.functions.map((func) => func.id));
          this.stepper.next();
        },
        error: (err) => {
          const message = err.error?.message ? err.error.message : 'Unable to load tools';
          this.snackBar.openErrorSnack(message);
        },
      });
  }

  toggleFunctionSelection(functionId: string, checked: boolean): void {
    if (checked) {
      this.selectedFunctionIds.add(functionId);
    } else {
      this.selectedFunctionIds.delete(functionId);
    }
  }

  saveMCP(): void {
    const selectedFunctions = this.functions.filter((func) => this.selectedFunctionIds.has(func.id));

    const upsertRequest$ = this.functionService.upsertMcp(
      new UpsertMCPRequest({
        namespace: this.data.namespace,
        mcpId: this.form.name.value,
        url: this.form.url.value,
        functions: selectedFunctions,
      }),
    );

    if (this.mcpExists) {
      this.confirmationModal
        .openModal({
          type: 'warn',
          title: this.translate.instant('AI_ASSISTANT.MCP.CONFIRMATION_TITLE'),
          message: this.translate.instant('AI_ASSISTANT.MCP.ALREADY_EXISTS'),
          confirmButtonText: this.translate.instant('AI_ASSISTANT.MCP.UPDATE_MCP'),
          cancelButtonText: this.translate.instant('AI_ASSISTANT.SHARED.CANCEL'),
        })
        .subscribe((confirmed) => {
          if (confirmed) {
            upsertRequest$.subscribe({
              next: () => {
                this.snackBar.openSuccessSnack(this.translate.instant('AI_ASSISTANT.MCP.TOOLS_SAVED'));
                this.dialogRef.close();
              },
              error: (err) => {
                const message = err.error?.message ? err.error.message : 'An error occurred while saving tools';
                this.snackBar.openErrorSnack(message);
              },
            });
          }
        });
      return;
    } else {
      upsertRequest$.subscribe({
        next: () => {
          this.snackBar.openSuccessSnack(this.translate.instant('AI_ASSISTANT.MCP.TOOLS_SAVED'));
          this.dialogRef.close();
        },
        error: (err) => {
          const message = err.error?.message ? err.error.message : 'An error occurred while saving tools';
          this.snackBar.openErrorSnack(message);
        },
      });
    }
  }
}
